import {
	IChatNotification,
	IChatPageConfiguration,
	IChatStateConfiguration,
	IChatTogglerConfiguration,
	ILocalizationConfiguration,
	IPoweredByConfiguration,
	ISignupConfiguration,
	IStyleConfiguration,
	ISurveyConfiguration,
	IThemeConfiguration,
} from 'src/types/configuration';
import type { IAttachUiConfiguration } from './attachUi';

export type IConfiguration = Partial<
	IConnectionConfiguration &
		IChatStateConfiguration &
		IThemeConfiguration &
		IPoweredByConfiguration &
		IChatPosition &
		IChatNotification & {
			m3_ep: string;
			fake_typing_delay?: number;
			localization: ILocalizationConfiguration;
			toggler_button?: IChatTogglerConfiguration;
			survey?: ISurveyConfiguration;
			chat?: IChatPageConfiguration;
			signup: ISignupConfiguration;
			/**
			 * Backend flag controlling whether file upload is actually allowed.
			 *
			 * @remarks
			 * This is a server-managed feature: the backend can overwrite
			 * `enabled` regardless of the value shipped in the client
			 * configuration, since file upload is a capability gated by the
			 * server. To control only the graphical attach affordances in the
			 * widget (without affecting the backend capability), use
			 * {@link IConfiguration.attach_ui} instead.
			 */
			file_upload: {
				enabled: boolean;
				max_size: string;
				allowed_types: Array<string>;
			};
			/**
			 * UI-only flag that hides the attach affordances (button, drag and
			 * drop, paste, and individual picker entries) without affecting the
			 * backend `file_upload` capability.
			 *
			 * @remarks
			 * This flag disables only the graphical part of the attach feature,
			 * and is authoritative for that: `file_upload` never overrides it.
			 * If this hides the attach UI (or a specific attach type), it stays
			 * hidden regardless of the current `file_upload.enabled` value —
			 * including when the backend flag is (or becomes) `true`. To fully
			 * disable the underlying file upload capability, contact the system
			 * administrator.
			 *
			 * See {@link IAttachUiConfiguration} for the accepted shape.
			 */
			attach_ui?: IAttachUiConfiguration;
			speech_input: boolean;
			transcript_input: boolean;
			/**
			 * When `true`, the messages list uses the experimental virtualised renderer
			 * (powered by `virtua`). Defaults to `false` (legacy renderer).
			 *
			 * @remarks
			 * This is a beta feature — enable with caution as it may present
			 * rendering artefacts in some environments.
			 */
			virtualized_messages_list?: boolean;
			/**
			 * Maximum number of renderable messages kept in the redux store for the
			 * legacy (reversed) renderer. When the user is at the bottom of the list,
			 * older messages beyond this count are removed and re-fetched on demand
			 * when the user scrolls up.
			 *
			 * - `undefined` — falls back to the built-in default (30 messages).
			 * - `-1` — disables trimming entirely.
			 */
			messages_trim_count?: number;
		}
>;

export type IConfiguratorConfiguration = IConfiguration & IStyleConfiguration;

export type IBaseConfiguratorConfiguration = Pick<
	IConfiguratorConfiguration,
	'state' | 'localization' | 'chat'
> & { id: string; name: string };

export type ConfigurationItem<
	T extends IBaseConfiguratorConfiguration = IBaseConfiguratorConfiguration,
> = {
	component: string;
	subconf: string;
	data: T;
};

export type IConnectionConfiguration = {
	websocket_url: string;
	http_url: string;
	echo_timeout?: number;
	reconnection_retries?: number;
	reconnection_timeout?: number;
	logger_key?: string;
	/**
	 * Page-scoped JWT session token issued by m3-gw-chat in the config response.
	 * It keeps concurrent tenant tabs independent from the shared `chat` cookie
	 * and also supports environments where third-party cookies are blocked.
	 *
	 * - Websocket: sent as the `Sec-WebSocket-Protocol` subprotocol.
	 * - HTTP APIs: sent as `Authorization: Bearer <jwt>`.
	 *
	 * When absent (old m3-gw-chat), the widget falls back to the
	 * cookie-only flow.
	 */
	jwt?: string;
};

export enum ChatXPosition {
	RIGHT = 'right',
	LEFT = 'left',
}

export type IChatPosition = Partial<{
	xPosition?: ChatXPosition;
}>;
